<template>
  <div class="ui page inverted dimmer"
       :class="{ active: isLoading }">
    <div class="ui text loader">Loading...</div>
  </div>

  <main-header v-if="showHeader"></main-header>

  <router-view></router-view>

  <div class="ui centered basic tiny modal"
       id="session-timeout-modal">
    <div class="ui icon header">
      <i class="clock outline icon"></i>
      Are you still there?
    </div>
    <div class="content">
      <p>For security reasons, your connection times out after you've been inactive for a while.</p>
      <p>Click <strong>Continue</strong> to stay signed in.</p>
      <div class="ui hidden divider"></div>
      <div class="ui yellow inverted tiny progress">
        <div class="bar"></div>
      </div>
    </div>
    <div class="centered actions">
      <button type="submit"
              class="ui right labeled icon primary ok button">
        <i class="right arrow icon"></i>
        Continue
      </button>
    </div>
  </div>

  <div id="app-version">v{{ appVersion }}</div>

  <div v-if="isAuthenticated && debugMode"
       class="ui red mini label"
       id="debug-label">
    <i class="bug icon"></i>
    DEBUG
    <div class="detail">
      {{ debugTokenSessInfo }}
    </div>
  </div>
</template>

<script>
import MainHeader from '@/components/MainHeader.vue'

import { createNamespacedHelpers } from 'vuex'
const { mapState, mapGetters } = createNamespacedHelpers('user')

import { EXIT_SITE } from '@/store/action-types'

const __DEBUG_TOKEN_EXP_MSECS = 60000 // 1min
const __DEBUG_SESS_EXP_MSECS = 30000 // .5min

const TOKEN_RENEWAL_PADDING = 10000 // 10s Padding - calls extendToken "EXPIRATION" - 10s

export default {
  data() {
    return {
      isLoading: false,
      APIErrorTimeout: null,
      currentView: null,
      strAPIError: '',
      advisoryMessage: null,
      errorToast: null,
      infoToast: null,
      appVersion: __APP_VERSION,
    }
  },
  components: {
    MainHeader,
  },
  computed: {
    ...mapState(['isAuthenticated']),
    ...mapGetters([
      'currentUser',
      'tasks',
      'hasInvestorPermissions',
      'getTokenExpirationMsecs',
      'getSessionExpirationMsecs',
    ]),
    showHeader() {
      return (
        this.isAuthenticated &&
        (!this.$route.path.includes('/error') ||
          this.$route.path === '/error/unauthorized') &&
        !this.$route.path.includes('/site-maintenance') &&
        !this.$route.fullPath.includes('p=1')
      )
    },
    debugMode() {
      // Combines the build env and an overwrite flag - to enable it comment out the '&& false' part
      return !import.meta.env.PROD && false
    },
    debugTokenSessInfo() {
      return `Token: ${this.debugMode ? __DEBUG_TOKEN_EXP_MSECS : this.getTokenExpirationMsecs
        }msecs | Session: ${this.debugMode ? __DEBUG_SESS_EXP_MSECS : this.getSessionExpirationMsecs
        }msecs`
    },
  },
  watch: {
    // Monitor the main "user.js" isAuthenticated flag for changes
    // in order to begin the timers
    isAuthenticated(authenticated) {
      if (authenticated && authenticated === true) {
        this.startClientSessionTimer()
        this.startTokenAutoRenewalTimer()
      } else {
        clearInterval(window.timeoutProgressInterval)
      }
    },
  },
  methods: {
    init(customView) {
      this.setCurrentView(customView)
    },
    setCurrentView(customView) {
      let _allTasks = []
      for (const set in this.tasks) {
        if (Object.hasOwnProperty.call(this.tasks, set)) {
          _allTasks = _allTasks.concat(this.tasks[set])
        }
      }

      this.currentView =
        customView ||
        _allTasks.find((el) => this.$route.path.startsWith(el.route))

      document.title = `${import.meta.env.VITE_APP_TITLE} - ${this.currentView
        ? this.currentView.label
        : 'Home'
        }`
    },
    /**
     * global showError toast - top center, etc.
     */
    showError(jqXHR, showDimmer = true) {
      let _dimmer = $('body').dimmer({
        closable: false,
        variation: 'light',
      })
      if (showDimmer) _dimmer.dimmer('show')

      let vm = this

      // Allows to show text-based UI error or XHR API error
      vm.strAPIError =
        typeof jqXHR !== 'object'
          ? jqXHR
          : vm.$utils.getAPIErrorMessageHTML(jqXHR)

      vm.$nextTick(() => {
        // Added a 'Contact Us' button to the error pop-up
        this.errorToast = $.toast({
          message: `<div class="ui all-caps mini inverted header">${vm.strAPIError}</div>`,
          preserveHTML: true,
          position: 'top center',
          closeIcon: true,
          class: `api-error-toast centered`,
          className: {
            toast: `ui error message`,
          },
          classActions: 'center attached',
          compact: false,
          actions: [
            {
              text: `Contact Us`,
              class: `mini red`,
              click: () => {
                window.open(SERVICE_DESK_URL, '_blank')
                return false
              },
            },
          ],
          classProgress: 'red',
          showProgress: 'bottom',
          displayTime: 0, // 30000,
          transition: {
            showMethod: 'fly down',
            showDuration: 700,
          },
          onHide() {
            if (showDimmer) _dimmer.dimmer('hide')
          },
        })
      })
    },
    hideError() {
      this.errorToast?.toast('close')
      this.errorToast?.toast('destroy')
      this.errorToast = null
      this.strAPIError = ''
    },
    /**
     * global showInfo toast - centered, optional dimmer (if dimmer, no auto hide, otherwise 30s auto hide), etc.
     */
    showInfo(message, showDimmer = true, showAction = true) {
      let _dimmer = $('body').dimmer({
        closable: false,
        variation: 'light',
      })
      if (showDimmer) _dimmer.dimmer('show')

      this.infoToast = $.toast({
        message: `<div class="ui all-caps tiny header"><i class="info icon"></i>${message.title}</div><p>${message.body}</p>`,
        preserveHTML: true,
        position: showDimmer ? 'centered' : 'top center',
        closeIcon: true,
        class: 'api-info-toast centered',
        className: {
          toast: `ui info message`,
        },
        classActions: 'center attached',
        compact: false,
        actions: showAction
          ? [
            {
              text: `Contact Us`,
              class: 'mini primary',
              click: () => {
                window.open(SERVICE_DESK_URL, '_blank')
                return false
              },
            },
          ]
          : null,
        onHide() {
          if (showDimmer) _dimmer.dimmer('hide')
        },
        classProgress: 'yellow',
        showProgress: 'bottom',
        displayTime: showDimmer ? 0 : 30000,
      })
    },
    hideInfo() {
      this.infoToast?.toast('close')
      this.infoToast?.toast('destroy')
      this.infoToast = null
    },
    signOutCurrentUser(event, redirectURL) {
      this.isLoading = true
      this.$store.dispatch(`user/${EXIT_SITE}`, redirectURL)
    },
    startClientSessionTimer() {
      let vm = this
      const TIME_TO_RESPOND_SECS = 60
      if (vm.getSessionExpirationMsecs) {
        $(document).idle({
          onIdle: function () {
            $('#session-timeout-modal')
              .modal({
                blurring: true,
                closable: false,
                onShow() {
                  let $progress = $('#session-timeout-modal .ui.progress')
                  $progress.progress({
                    total: TIME_TO_RESPOND_SECS,
                    value: TIME_TO_RESPOND_SECS - 1,
                    showActivity: false,
                    autoSuccess: false,
                    onChange(percent, value) {
                      // 60 seconds expired. Force Logout.
                      if (value && value[0] === 0) {
                        clearInterval(window.timeoutProgressInterval)
                        $('#session-timeout-modal').modal('hide')
                        // Due to this function being used as an event handler
                        // for the Sign Out link, the 1st param is event object
                        // injected by vuejs
                        vm.signOutCurrentUser(null, '/error/session-expired')
                      }
                    },
                  })
                },
                onVisible() {
                  let $progress = $('#session-timeout-modal .ui.progress')
                  window.timeoutProgressInterval = setInterval(function () {
                    $progress.progress('decrement')
                  }, 1000)
                },
                onApprove() {
                  vm.extendToken()
                  clearInterval(window.timeoutProgressInterval)
                  $('#session-timeout-modal').modal('hide')
                },
              })
              .modal('show')
          },
          idle: vm.debugMode
            ? __DEBUG_SESS_EXP_MSECS
            : vm.getSessionExpirationMsecs,
        })
      }
    },
    startTokenAutoRenewalTimer() {
      let vm = this

      if (vm.getTokenExpirationMsecs) {
        // On APP LOADING / HARD REFRESH call to extend token so it syncs with the Interval below
        vm.extendToken(false)

        // Start an internval to request new token every 'TokenExpirationMsecs'
        window.tokenAutoRenewInterval = setInterval(
          vm.extendToken,
          vm.debugMode
            ? __DEBUG_TOKEN_EXP_MSECS - TOKEN_RENEWAL_PADDING
            : vm.getTokenExpirationMsecs - TOKEN_RENEWAL_PADDING,
        )
      }
    },
    extendToken(async = true) {
      // console.log('extendToken: ' + JSON.stringify(new Date()))

      // Call Extend if and only if we still have a cookie with XSRF token in it, otherwise ignore
      if (this.$utils.getXSRFTokenFromCookie()) {
        $.ajax({
          url: `${AUTH_URL}/token/extend`,
          method: 'PUT',
          async: async,
        })
        // .done(function () {
        //   console.log('extendToken Done: ' + JSON.stringify(new Date()))
        // })
      }
    },
  },
  mounted() {
    $('body').addClass('midas')

    if (__ENV !== 'PRODUCTION') {
      // eslint-disable-next-line no-console
      console.log('Environment Variables:', import.meta.env)
    }
  },
}
</script>

<style lang="stylus">
[v-cloak] {
  display: none;
}

.midas {
  background-image: url('./assets/images/MIDAS/midas-globe-gold-hi-translucent.png') !important;
  background-repeat: no-repeat !important;
  background-position: bottom -45% right -8% !important;
  background-position: bottom -120px right -40px !important;
  background-size: 30% !important;
  background-size: 25% !important;
  background: $dark-white;
  background-attachment: fixed;
}

::selection {
  background-color: $highlight-color !important;
}

/*
.ui.card {
  background: transparentify(#fff, 0.95) !important;
} */
.ui.selectable.card {
  border: 1px solid transparent !important;

  &:hover {
    border: 1px solid $midas-blue !important;
  }

  &.active {
    border: 1px solid $midas-darkgold !important;
  }
}

.ui.secondary.segment {
  background: transparentify(#f3f4f5, 0.95) !important;
}

.clearing {
  clear: both;
}

form label {
  cursor: pointer;
}

// TODO
div[id*='-view-root'] {
  min-height: calc(100vh - 66px);
  position: relative;
  padding: 1rem;
}

#app {
  height: inherit;
}

.api-info-toast {
  max-width: 586px;

  .ui.label {
    vertical-align: middle !important;
  }
}

.api-error-toast {
  // PLACEHOLDER
}

.api-info-toast,
.api-error-toast {
  +above(768px) {
    padding: 1rem 3rem !important;
  }
}

/* see above */
i.icon,
i.icons {
  vertical-align: baseline !important;
}

/* style for all button elements, elements with gts-btn class,
  or div.button (e.g. semantic modals)
*/
.gts-btn,
button,
div.button {
  click-down();
  vendor('user-select', none);
  cursor: pointer;
}

.bold-text {
  font-weight: bold !important;
}

.emphasized-text {
  cursor: default;
  color: $midas-blue !important;
  border-bottom: none !important;
}

.ui.modal:not(.basic) {
  > .header {
    background-color: $gts-lightgray !important;
    text-shadow();
    text-transform: uppercase;
    font-size: 1rem !important;

    h3 {
      font-size: 1.45rem;
      text-align: center;
    }
  }

  .actions > .button {
    margin-left: 0 !important;
  }
}

.ui.modal {
  .actions {
    padding: 0.75rem 1rem;
  }
}

.dx-data-row.dx-state-hover > td {
  background-color: darken($gts-lightgray, 5%) !important;
}

#debug-label {
  position: fixed;
  z-index: 999999 !important;
  right: 5rem;
  bottom: 1rem;
  opacity: 0.5;

  &:hover {
    opacity: 1;
  }
}

#app-version {
  position: absolute;
  color: $dark-white;
  bottom: 0;
  left: 8px;
  font-size: 8px;
}

.dirty {
  background: $dirty-field !important;
}

/* overwriting semantic-ui error message look and feel */
.ui.form .ui.error.message {
  margin: 0 !important;
  padding: 0.25rem 0 !important;
  box-shadow: none !important;
  background: none;
  font-weight: 300 !important;
}

/* shake animation */
@keyframes shakeError {
  0% {
    transform: translateX(0);
  }

  15% {
    transform: translateX(0.375rem);
  }

  30% {
    transform: translateX(-0.375rem);
  }

  45% {
    transform: translateX(0.375rem);
  }

  60% {
    transform: translateX(-0.375rem);
  }

  75% {
    transform: translateX(0.375rem);
  }

  90% {
    transform: translateX(-0.375rem);
  }

  100% {
    transform: translateX(0);
  }
}

.ui.form {
  .input-error--message {
    display: none;
    color: $red;
    // position: absolute;
    font-size: 0.7rem;
    left: 0;
    bottom: -1.75em;
    white-space: nowrap;
  }

  .field.error {
    position: relative;

    /* overwriting Semantic prompt label for errors */
    .prompt.label {
      background: $red !important;
      color: #FFF !important;
      border: 1px solid darken(#db2828, 20%) !important;
    }

    .prompt.floating.label {
      top: 0 !important;
    }

    .input-error--message {
      display: block;
      animation-name: shakeError;
      animation-duration: 0.6s;
      animation-timing-function: ease-in-out;
    }
  }

  .fields > .field.error {
    .input-error--message {
      left: 0.5rem;
    }
  }
}

.helper-text {
  text-transform: initial !important;
  font-size: 0.85rem !important;
  color: $gts-text-color !important;
  font-weight: normal !important;

  &.inverted {
    color: $lightgray-color !important;
  }
}

.inverted .helper-text {
  color: $lightgray-color;
}

.centered {
  text-align: center !important;
}

.floated-left {
  float: left;
}

.floated-right {
  float: right;
}

.gts-subview-header {
  font-size: 1.5rem !important;
  text-transform: uppercase !important;
  margin-top: -0.14285714em !important;

  > .sub.header {
    text-transform: none !important;
  }

  > .floated-right.ui.label {
    /* vertical alignment - TODO */
    margin-top: 0.25rem !important;
  }
}

.all-caps {
  text-transform: uppercase !important;
}

input.all-caps::placeholder {
  text-transform: none;
}

.header-cell {
  background-color: $gts-lightgray;
  font-weight: bold;
}

.header-cell--wrap {
  white-space: normal !important;
}

.header-cell--nowrap {
  white-space: nowrap;
}

.subsection-title {
  font-size: 1.15rem !important;
  text-transform: uppercase !important;

  &.dividing {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  }

  > span {
    font-weight: normal;
    font-size: 0.8rem;
    display: inline-block;
    line-height: 1;
    margin-left: 1.5rem;
    color: $gts-text-color;
  }
}

.display-none {
  display: none !important;
}

.display-inline-block {
  display: inline-block;
}

.hidden-visibly {
  visibility: hidden;
}

.abs-center {
  justify-content: center; // horizontally centers single line items
  text-align: center; // optional, but helps horizontally center text that breaks into multiple lines
  display: flex; // make us of Flexbox
  align-items: center; // does vertically center the desired content
}

.italics {
  font-style: italic;
}

.no-padding {
  padding: 0 !important;
}

.no-margin {
  margin: 0 !important;
}

.break-word {
  word-break: break-word;
  word-wrap: break-word;
}

.with-hover-text {
  cursor: help !important;
}

.cursor.pointer {
  cursor: pointer;
}

.overflown {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
